import React, { useEffect, useState, useCallback } from "react";
import HeartLoader from "@/components/HeartLoader";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck, Search, Loader2, Lock, Unlock, Download, Mail, Building2, Upload,
  FileText, AlertTriangle, ListChecks, Gavel, Users, ExternalLink, X, Sparkles, Coins, ScrollText,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, RadialBarChart, RadialBar, PolarAngleAxis, LabelList,
} from "recharts";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const SEV = { high: "#ef4444", medium: "#f59e0b", low: "#10b981" };
const SEV_BG = { high: "bg-red-500/15 text-red-400", medium: "bg-amber-500/15 text-amber-400", low: "bg-emerald-500/15 text-emerald-400" };
const SCORE_LABELS = {
  complexity: "Complexity", enforcement_strength: "Enforcement", penalty_severity: "Penalty severity",
  data_sensitivity: "Data sensitivity", implementation_effort: "Implementation effort",
};

function ScoreBars({ scores }) {
  const data = Object.entries(scores || {}).map(([k, v]) => ({ name: SCORE_LABELS[k] || k, value: Math.max(0, Math.min(100, Number(v) || 0)) }));
  if (!data.length) return null;
  const color = (v) => (v >= 67 ? SEV.high : v >= 34 ? SEV.medium : SEV.low);
  return (
    <div className="rud-card rounded-2xl p-5" data-testid="score-bars">
      <div className="text-xs uppercase tracking-widest text-brand mb-3 flex items-center gap-1.5"><Sparkles size={13} /> Risk &amp; effort profile</div>
      <ResponsiveContainer width="100%" height={data.length * 44 + 10}>
        <BarChart layout="vertical" data={data} margin={{ left: 8, right: 40, top: 4, bottom: 4 }}>
          <XAxis type="number" domain={[0, 100]} hide />
          <YAxis type="category" dataKey="name" width={130} tick={{ fill: "var(--rud-text-sec)", fontSize: 12 }} axisLine={false} tickLine={false} />
          <Bar dataKey="value" radius={[6, 6, 6, 6]} barSize={18}>
            {data.map((d, i) => <Cell key={i} fill={color(d.value)} />)}
            <LabelList dataKey="value" position="right" fill="var(--rud-text)" fontSize={12} formatter={(v) => `${v}`} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Gauge({ score, label = "Readiness" }) {
  const v = Math.max(0, Math.min(100, Number(score) || 0));
  const color = v >= 70 ? SEV.low : v >= 40 ? SEV.medium : SEV.high;
  return (
    <div className="rud-card rounded-2xl p-5 flex flex-col items-center justify-center" data-testid="readiness-gauge">
      <div className="text-xs uppercase tracking-widest text-brand mb-1">{label}</div>
      <div className="relative">
        <ResponsiveContainer width={160} height={140}>
          <RadialBarChart innerRadius="72%" outerRadius="100%" data={[{ value: v, fill: color }]} startAngle={210} endAngle={-30}>
            <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
            <RadialBar background={{ fill: "var(--rud-border)" }} dataKey="value" cornerRadius={10} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <div className="font-serif text-3xl" style={{ color }}>{v}</div>
          <div className="text-[10px] text-sec">/ 100</div>
        </div>
      </div>
    </div>
  );
}

function RiskSeveritySummary({ risks }) {
  const counts = { high: 0, medium: 0, low: 0 };
  (risks || []).forEach((r) => { counts[r.severity] = (counts[r.severity] || 0) + 1; });
  return (
    <div className="flex items-center gap-2" data-testid="risk-severity-summary">
      {["high", "medium", "low"].map((s) => (
        <div key={s} className={`px-3 py-2 rounded-xl text-center ${SEV_BG[s]}`}>
          <div className="font-serif text-xl leading-none">{counts[s]}</div>
          <div className="text-[10px] uppercase tracking-wide capitalize">{s}</div>
        </div>
      ))}
    </div>
  );
}

const Section = ({ icon: Icon, title, items }) => (!items || !items.length) ? null : (
  <div>
    <div className="text-sm font-semibold flex items-center gap-2 mb-2"><Icon size={15} className="text-brand" /> {title}</div>
    <ul className="space-y-1.5">
      {items.map((x, i) => <li key={i} className="text-sm text-sec flex gap-2"><span className="text-brand mt-1">•</span><span>{typeof x === "string" ? x : JSON.stringify(x)}</span></li>)}
    </ul>
  </div>
);

function RiskTable({ risks }) {
  if (!risks || !risks.length) return null;
  return (
    <div data-testid="risk-table">
      <div className="text-sm font-semibold flex items-center gap-2 mb-2"><AlertTriangle size={15} className="text-brand" /> Risks &amp; mitigations</div>
      <div className="space-y-2">
        {risks.map((r, i) => (
          <div key={i} className="rud-card rounded-xl p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="text-sm font-medium">{r.risk}</div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase shrink-0 ${SEV_BG[r.severity]}`}>{r.severity}</span>
            </div>
            <div className="text-xs text-sec mt-1"><b className="text-[var(--rud-text)]">Mitigation:</b> {r.mitigation}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DetailBody({ std, detail, user, setUser, onOrgPlan }) {
  const ref = detail.official_reference;
  const key = std ? { code: std.code } : {};
  const [busy, setBusy] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [email, setEmail] = useState(user?.email || "");

  const download = async () => {
    setBusy("pdf");
    try {
      const res = await api.post("/compliance/pdf", key, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `My Kind-${(std?.name || "Compliance")}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      if (user) setUser({ ...user, credits: (user.credits || 0) - 10 });
      toast.success("PDF downloaded · 10 credits");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const share = async () => {
    if (!email.trim()) { toast.error("Enter an email."); return; }
    setBusy("share");
    try {
      const { data } = await api.post("/compliance/share", { ...key, email: email.trim() });
      if (data.credits_remaining != null && user) setUser({ ...user, credits: data.credits_remaining });
      toast.success("Interpretation emailed · 2 credits");
      setShareOpen(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-5" data-testid="compliance-detail">
      {ref && (
        <div className="rud-card rounded-2xl p-5" data-testid="official-reference">
          <div className="text-xs uppercase tracking-widest text-brand mb-2 flex items-center gap-1.5"><ScrollText size={13} /> Official source &amp; regulation overview</div>
          <div className="text-sm"><b>{ref.full_name}</b></div>
          <div className="text-xs text-sec mt-1">Authority: {ref.agency} · Jurisdiction: {ref.jurisdiction} · Effective: {ref.effective}</div>
          {detail.scope_overview && <p className="text-sm text-sec mt-2">{detail.scope_overview}</p>}
          <a href={ref.official_url} target="_blank" rel="noopener noreferrer" data-testid="official-link" className="inline-flex items-center gap-1.5 text-sm text-brand hover:underline mt-2">
            <ExternalLink size={13} /> Read the verbatim official text
          </a>
        </div>
      )}

      {detail.summary && (
        <div className="rud-card rounded-2xl p-5">
          <div className="text-sm font-semibold mb-1">Summary</div>
          <p className="text-sm text-sec">{detail.summary}</p>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4 items-start">
        <div className="md:col-span-2"><ScoreBars scores={detail.scores} /></div>
        <div className="rud-card rounded-2xl p-5 self-start">
          <div className="text-xs uppercase tracking-widest text-brand mb-3 flex items-center gap-1.5"><AlertTriangle size={13} /> Risk exposure</div>
          <RiskSeveritySummary risks={detail.risks} />
          <p className="text-xs text-sec mt-3">{(detail.risks || []).length} risks identified across severity levels.</p>
        </div>
      </div>

      <div className="rud-card rounded-2xl p-5 grid sm:grid-cols-2 gap-5">
        <Section icon={Users} title="Who it applies to" items={detail.applies_to} />
        <Section icon={ShieldCheck} title="Must-haves (non-negotiable)" items={detail.must_haves} />
        <Section icon={ListChecks} title="Key obligations" items={detail.key_obligations} />
        <Section icon={ListChecks} title="Action checklist — what you must do" items={detail.action_checklist} />
      </div>

      {detail.penalties && (
        <div className="rud-card rounded-2xl p-5">
          <div className="text-sm font-semibold flex items-center gap-2 mb-1"><Gavel size={15} className="text-brand" /> Penalties &amp; enforcement</div>
          <p className="text-sm text-sec">{detail.penalties}</p>
        </div>
      )}

      <div className="rud-card rounded-2xl p-5"><RiskTable risks={detail.risks} /></div>

      {detail.disclaimer && <p className="text-xs text-sec italic">{detail.disclaimer}</p>}

      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="download-pdf" onClick={download} disabled={busy === "pdf"} className="btn-primary h-10 px-4 flex items-center gap-2 disabled:opacity-60">
          {busy === "pdf" ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download PDF <span className="text-xs opacity-80">· 10 cr</span>
        </button>
        <button data-testid="share-open" onClick={() => setShareOpen((s) => !s)} className="btn-ghost h-10 px-4 rounded-full flex items-center gap-2">
          <Mail size={15} /> Email / Share <span className="text-xs opacity-80">· 2 cr</span>
        </button>
        <button data-testid="org-plan-open" onClick={onOrgPlan} className="ml-auto h-10 px-4 rounded-full flex items-center gap-2 border border-brand/40 bg-brand/5 text-brand hover:bg-brand/10 transition-colors">
          <Building2 size={15} /> Study risks &amp; mitigations for your org
        </button>
      </div>

      <AnimatePresence>
        {shareOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
            <div className="rud-card rounded-2xl p-4 flex flex-wrap items-center gap-2" data-testid="share-form">
              <input data-testid="share-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Recipient email" className="rud-input rounded-lg px-3 py-2.5 text-sm flex-1 min-w-[220px]" />
              <button data-testid="share-send" onClick={share} disabled={busy === "share"} className="btn-primary h-10 px-4 flex items-center gap-2 disabled:opacity-60">
                {busy === "share" ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} Send · 2 cr
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function OrgPlanView({ plan }) {
  const maturity = (plan.maturity_by_area || []).map((m) => ({ name: m.area, value: Math.max(0, Math.min(100, Number(m.score) || 0)) }));
  return (
    <div className="space-y-5" data-testid="org-plan-view">
      <div className="grid md:grid-cols-3 gap-4 items-stretch">
        <Gauge score={plan.readiness_score} label="Org readiness" />
        <div className="md:col-span-2 rud-card rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-brand mb-3">Maturity by area</div>
          {maturity.length ? (
            <ResponsiveContainer width="100%" height={maturity.length * 52 + 10}>
              <BarChart layout="vertical" data={maturity} margin={{ left: 4, right: 40 }}>
                <XAxis type="number" domain={[0, 100]} hide />
                <YAxis type="category" dataKey="name" width={170} tick={{ fill: "var(--rud-text-sec)", fontSize: 11, width: 165 }} axisLine={false} tickLine={false} interval={0} />
                <Bar dataKey="value" radius={[6, 6, 6, 6]} barSize={16} fill="#0b7285">
                  {maturity.map((m, i) => <Cell key={i} fill={m.value >= 70 ? SEV.low : m.value >= 40 ? SEV.medium : SEV.high} />)}
                  <LabelList dataKey="value" position="right" fill="var(--rud-text)" fontSize={11} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="text-sm text-sec">—</div>}
        </div>
      </div>

      {plan.personalised_interpretation && (
        <div className="rud-card rounded-2xl p-5">
          <div className="text-sm font-semibold mb-1">Personalised interpretation</div>
          <p className="text-sm text-sec">{plan.personalised_interpretation}</p>
        </div>
      )}

      {(plan.gaps || []).length > 0 && (
        <div className="rud-card rounded-2xl p-5">
          <div className="text-sm font-semibold flex items-center gap-2 mb-3"><AlertTriangle size={15} className="text-brand" /> Gap analysis</div>
          <div className="space-y-2">
            {plan.gaps.map((g, i) => (
              <div key={i} className="rud-card rounded-xl p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium">{g.area}</div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase ${SEV_BG[g.severity]}`}>{g.severity}</span>
                </div>
                <div className="text-xs text-sec mt-1"><b className="text-[var(--rud-text)]">Now:</b> {g.current_state} · <b className="text-[var(--rud-text)]">Required:</b> {g.required_state}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(plan.risk_register || []).length > 0 && (
        <div className="rud-card rounded-2xl p-5">
          <div className="text-sm font-semibold flex items-center gap-2 mb-3"><AlertTriangle size={15} className="text-brand" /> Risk register</div>
          <div className="space-y-2">
            {plan.risk_register.map((r, i) => (
              <div key={i} className="rud-card rounded-xl p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm font-medium">{r.risk}</div>
                  <div className="flex items-center gap-1 shrink-0">
                    {r.priority && <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand/15 text-brand">{r.priority}</span>}
                    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase ${SEV_BG[r.severity]}`}>{r.severity}</span>
                  </div>
                </div>
                <div className="text-xs text-sec mt-1"><b className="text-[var(--rud-text)]">Mitigation:</b> {r.mitigation}{r.owner ? ` · Owner: ${r.owner}` : ""}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Section icon={ListChecks} title="Action plan" items={(plan.action_plan || []).map((a) => `${a.step}${a.timeline ? ` — ${a.timeline}` : ""}${a.effort ? ` (${a.effort} effort)` : ""}`)} />
      {plan.disclaimer && <p className="text-xs text-sec italic">{plan.disclaimer}</p>}
    </div>
  );
}

function OrgPlanModal({ pricing, onClose, onDone }) {
  const { user, setUser } = useApp();
  const [size, setSize] = useState("small");
  const [context, setContext] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const labels = pricing?.org_labels || { personal: "Individual", small: "Small Org", mid: "Mid-size Org", enterprise: "Enterprise" };
  const prices = pricing?.org_plan || { personal: 1000, small: 5000, mid: 12000, enterprise: 25000 };

  const submit = async () => {
    setBusy(true);
    const fd = new FormData();
    fd.append("org_size", size);
    fd.append("context", context);
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/compliance/org-plan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.credits_remaining != null && user) setUser({ ...user, credits: data.credits_remaining });
      toast.success(`Org plan ready · ${data.charged} credits`);
      onDone(data.plan);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} onClick={(e) => e.stopPropagation()}
        className="rud-card rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="org-plan-modal">
        <div className="flex items-center justify-between mb-3">
          <div className="font-serif text-2xl flex items-center gap-2"><Building2 size={20} className="text-brand" /> Org Risk &amp; Mitigation Plan</div>
          <button onClick={onClose} className="text-sec hover:text-[var(--rud-text)]"><X size={18} /></button>
        </div>
        <p className="text-sm text-sec mb-4">Upload your SOPs, policies & compliance material. My Kind builds a personalised gap analysis, risk register and mitigation plan for your organisation.</p>

        <div className="text-xs uppercase tracking-widest text-sec mb-2">Organisation size — pricing scales with size</div>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {["personal", "small", "mid", "enterprise"].map((k) => (
            <button key={k} data-testid={`orgplan-size-${k}`} onClick={() => setSize(k)}
              className={`rounded-xl p-3 text-left border transition-colors ${size === k ? "border-brand bg-brand/10 text-brand" : "border-rud text-sec hover:border-brand/50"}`}>
              <div className="text-sm font-medium">{labels[k]}</div>
              <div className="text-[11px] flex items-center gap-1"><Coins size={11} /> {prices[k].toLocaleString()} credits</div>
            </button>
          ))}
        </div>

        <input data-testid="orgplan-context" value={context} onChange={(e) => setContext(e.target.value)} placeholder="Target standards / context (e.g. GDPR + ISO 27001)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mb-3" />

        <label className="block rud-card rounded-xl p-4 border-dashed cursor-pointer text-center mb-4" data-testid="orgplan-dropzone">
          <input type="file" multiple className="hidden" data-testid="orgplan-files" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
          <Upload size={20} className="text-brand mx-auto mb-1" />
          <div className="text-sm">{files.length ? `${files.length} file(s) selected` : "Upload SOPs / policies / compliance docs"}</div>
          <div className="text-[11px] text-sec mt-0.5">PDF, Word, Excel, PPT, images — up to 8 files</div>
        </label>

        <button data-testid="orgplan-submit" onClick={submit} disabled={busy} className="btn-primary w-full h-11 flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <><Loader2 size={16} className="animate-spin" /> Building your plan…</> : <>Generate plan · {prices[size].toLocaleString()} credits</>}
        </button>
      </motion.div>
    </div>
  );
}

export default function Compliance() {
  const { user, setUser } = useApp();
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("");
  const [category, setCategory] = useState("");
  const [standards, setStandards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // {std, detail, unlocked}
  const [detailLoading, setDetailLoading] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [orgModal, setOrgModal] = useState(false);
  const [orgPlan, setOrgPlan] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);

  useEffect(() => { api.get("/compliance/meta").then(({ data }) => setMeta(data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/compliance/directory", { params: { q, region, category } });
      setStandards(data.standards || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [q, region, category]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openStandard = useCallback(async (code) => {
    setDetailLoading(true); setSelected(null); setOrgPlan(null);
    try {
      const { data } = await api.get(`/compliance/standard/${code}`);
      setSelected({ std: data.standard, detail: data.detail, unlocked: data.unlocked });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setDetailLoading(false); }
  }, []);

  // Deep-link from Engine chips: /compliance?standard=<code> or ?name=<free text>
  useEffect(() => {
    const code = params.get("standard");
    const name = params.get("name");
    if (code) { openStandard(code); }
    else if (name) { api.get("/compliance/resolve", { params: { name } }).then(({ data }) => data.code && openStandard(data.code)).catch(() => {}); }
    // eslint-disable-next-line
  }, []);

  const unlock = async () => {
    if (!user) { toast.error("Sign in to unlock interpretations."); return; }
    setUnlocking(true);
    try {
      const { data } = await api.post(`/compliance/standard/${selected.std.code}/interpret`);
      if (data.credits_remaining != null) setUser({ ...user, credits: data.credits_remaining });
      setSelected((s) => ({ ...s, detail: data.detail, unlocked: true }));
      setStandards((arr) => arr.map((x) => x.code === selected.std.code ? { ...x, unlocked: true } : x));
      toast.success(data.charged > 0 ? `Interpretation unlocked · ${data.charged} credits` : "Interpretation opened");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setUnlocking(false); }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    if (!user) { toast.error("Sign in to interpret your document."); return; }
    setUploadBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/compliance/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.credits_remaining != null) setUser({ ...user, credits: data.credits_remaining });
      setSelected({ std: { code: null, name: data.title, full_name: data.title, category: "Uploaded document" }, detail: data.detail, unlocked: true, uploadId: data.upload_id });
      setOrgPlan(null);
      toast.success(`Document interpreted · ${data.charged} credits`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setUploadBusy(false); }
  };

  const back = () => { setSelected(null); setOrgPlan(null); if (params.get("standard") || params.get("name")) setParams({}); };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-28 pb-24">
      <div className="max-w-3xl">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><ShieldCheck size={14} /> Compliance Engine</span>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter mt-3">Every regulation, read for you.</h1>
        <p className="text-sec mt-3">A global directory of compliance standards — with plain-English interpretations, must-haves, penalties, action checklists and risk-mitigation plans. Upload your own docs or build a personalised plan for your organisation.</p>
      </div>

      {!selected ? (
        <>
          {/* Search + filters */}
          <div className="flex flex-wrap items-center gap-2 mt-8">
            <div className="relative flex-1 min-w-[240px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sec" />
              <input data-testid="compliance-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search GDPR, HIPAA, ISO 27001, PCI DSS…" className="rud-input w-full rounded-full pl-10 pr-4 py-2.5 text-sm" />
            </div>
            <select data-testid="compliance-region" value={region} onChange={(e) => setRegion(e.target.value)} className="rud-input rounded-lg px-3 py-2.5 text-sm">
              <option value="">All regions</option>
              {(meta?.regions || []).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select data-testid="compliance-category" value={category} onChange={(e) => setCategory(e.target.value)} className="rud-input rounded-lg px-3 py-2.5 text-sm">
              <option value="">All categories</option>
              {(meta?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Upload your own */}
          <label className="mt-4 block rud-card rounded-2xl p-4 border-dashed cursor-pointer" data-testid="compliance-upload">
            <input type="file" className="hidden" data-testid="compliance-upload-input" disabled={uploadBusy} onChange={(e) => uploadDoc(e.target.files?.[0])} />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-brand/15 grid place-items-center text-brand shrink-0">{uploadBusy ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}</div>
              <div className="flex-1">
                <div className="text-sm font-medium">Upload your own compliance document</div>
                <div className="text-xs text-sec">My Kind reads it and tells you the must-haves, actions, risks & mitigations · {meta?.pricing?.interpret ?? 100} credits</div>
              </div>
              <FileText size={16} className="text-sec" />
            </div>
          </label>

          {/* Directory */}
          {loading ? (
            <div className="text-center py-16 text-sec"><HeartLoader /></div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4" data-testid="compliance-directory">
              {standards.map((s) => (
                <button key={s.code} data-testid={`std-${s.code}`} onClick={() => openStandard(s.code)}
                  className="rud-card rounded-2xl p-4 text-left hover:ring-1 hover:ring-brand/40 transition group">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-serif text-lg leading-tight">{s.name}</div>
                    {s.unlocked ? <Unlock size={14} className="text-emerald-400 shrink-0 mt-1" /> : <Lock size={14} className="text-sec shrink-0 mt-1" />}
                  </div>
                  <div className="text-[11px] text-brand mt-0.5">{s.category} · {s.region}</div>
                  <div className="text-xs text-sec mt-2 line-clamp-2">{s.one_liner}</div>
                  <div className="text-[11px] text-sec mt-2">{s.agency}</div>
                </button>
              ))}
              {!standards.length && <div className="col-span-full text-center text-sec text-sm py-10">No standards match your search.</div>}
            </div>
          )}
        </>
      ) : (
        <div className="mt-8">
          <button data-testid="compliance-back" onClick={back} className="btn-ghost rounded-full h-9 px-3 text-sm flex items-center gap-1.5 mb-4">← Back to directory</button>

          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div>
              <h2 className="font-serif text-3xl tracking-tight" data-testid="detail-title">{selected.std.name}</h2>
              <div className="text-sm text-sec">{selected.std.full_name}{selected.std.category ? ` · ${selected.std.category}` : ""}</div>
            </div>
          </div>

          {detailLoading ? (
            <div className="text-center py-16 text-sec"><HeartLoader /></div>
          ) : orgPlan ? (
            <div>
              <button data-testid="orgplan-back" onClick={() => setOrgPlan(null)} className="btn-ghost rounded-full h-9 px-3 text-sm mb-4">← Back to interpretation</button>
              <OrgPlanView plan={orgPlan} />
            </div>
          ) : selected.unlocked && selected.detail ? (
            <DetailBody std={selected.std} detail={selected.detail} user={user} setUser={setUser} onOrgPlan={() => setOrgModal(true)} />
          ) : (
            <div className="rud-card rounded-2xl p-8 text-center" data-testid="locked-panel">
              <div className="w-14 h-14 rounded-2xl bg-brand/15 grid place-items-center text-brand mx-auto mb-3"><Lock size={24} /></div>
              <div className="font-serif text-xl">Unlock the full interpretation</div>
              <p className="text-sm text-sec mt-1 max-w-md mx-auto">Get the plain-English summary, must-haves, penalties, action checklist and a full risk & mitigation profile with visual scores.</p>
              <p className="text-xs text-sec mt-2">{selected.std.one_liner}</p>
              <button data-testid="unlock-btn" onClick={unlock} disabled={unlocking} className="btn-primary h-11 px-6 mt-4 inline-flex items-center gap-2 disabled:opacity-60">
                {unlocking ? <Loader2 size={16} className="animate-spin" /> : <Unlock size={16} />} Unlock interpretation · {meta?.pricing?.interpret ?? 100} credits
              </button>
            </div>
          )}
        </div>
      )}

      <AnimatePresence>
        {orgModal && <OrgPlanModal pricing={meta?.pricing} onClose={() => setOrgModal(false)} onDone={(plan) => { setOrgModal(false); setOrgPlan(plan); window.scrollTo({ top: 0, behavior: "smooth" }); }} />}
      </AnimatePresence>
    </div>
  );
}
